import { useMount } from 'ahooks';
import { message } from 'antd';
import { AppStore } from '@/store';
import { getPresetsThemeColor, hexToRgba } from '@/utils';
import queryDomMethods from '@/utils/queryDomMethods';
import { IMicroAppProps, INoticeData } from '@/types';
import globalStore from '@/store/global';
import i18n from '@/i18n';

const useAppInitialization = (props: IMicroAppProps, store: AppStore) => {
    const isMicroApp = window.__POWERED_BY_QIANKUN__;
    const microAppType = props.microAppType;
    const mainAppBaseUrl = window.qiankunOptions?.baseUrl;
    const { themeStore, commonStore } = store;

    const initMicroApp: () => Function | undefined = () => {
        commonStore.setConfig({ bookingUrl: props.bookingUrl || '' });

        if (isMicroApp && microAppType) {
            message.config({ getContainer: queryDomMethods.querySubTikuRoot, prefixCls: 'ant5-message' });
            return;
        }
        if (isMicroApp && mainAppBaseUrl) {
            const { onReceiveMessageFromMicroApp, registryNoticeMicroAppFn } = props;
            // 父应用接受消息
            if (onReceiveMessageFromMicroApp) {
                globalStore.subscribe('main-app', onReceiveMessageFromMicroApp);
                globalStore.publish('main-app', { type: 'success', content: '通信已建立!' });
            }
            /* 父应用传递消息 */
            // registryNoticeMicroAppFn?.(({ type, params = {} }: INoticeData) => {
            //     // globalStore.publish(microAppType, { type, params });
            // });

            return () => globalStore.unsubscribe('main-app');
        }
        return;
    };

    const initSystemTheme: () => Function | undefined = () => {
        const themeConfig = typeof props.theme === 'string' ? getPresetsThemeColor(props.theme) : props.theme || {};
        const { mainColor, secondaryColor } = { ...themeStore.theme, ...themeConfig };

        const root = document.documentElement;
        root.style.setProperty('--main-color', mainColor);
        root.style.setProperty('--secondary-color', secondaryColor);

        // 生成 main-color 的透明度从 0.9 到 0.1 的色值，并设置为 CSS 变量
        const mainColorAlpha: Record<string, string> = {};
        for (let i = 9; i >= 1; i--) {
            const opacity = i / 10;
            const alphaColor = hexToRgba(mainColor, opacity);
            root.style.setProperty(`--main-color-alpha-${i}`, alphaColor);
            mainColorAlpha[`alpha{i}`] = alphaColor; // 将透明度色值存储到对象中
            root.style.setProperty(`--main-color-alpha-${i}`, hexToRgba(mainColor, opacity));
        }

        const rootSize = localStorage.getItem('subTikuSize') || '16px';
        root.style.setProperty('--root-size', rootSize);

        const rootLineHeight = localStorage.getItem('subTikuLineHeight') || '30px';
        root.style.setProperty('--root-line-height', rootLineHeight);

        // 更新 themeStore，包括 mainColorAlpha
        themeStore.setTheme({ mainColor, secondaryColor, rootSize, mainColorAlpha });

        return () => {
            // 清理主题时，可以将样式重置为默认值或移除样式
            root.style.removeProperty('--main-color');
            root.style.removeProperty('--secondary-color');
            for (let i = 9; i >= 1; i--) {
                root.style.removeProperty(`--main-color-alpha-${i}`);
            }
            root.style.removeProperty('--root-size');
        };
    };

    useMount(() => {
        const unMountMicroAppCallback = initMicroApp();
        const unMountSystemThemeCallback = initSystemTheme();

        if (i18n.language === 'en') { // 英文版本处理字体
            const subTikuEl = document.getElementById('sub-tiku');
            if (subTikuEl) {
                subTikuEl.style.fontFamily = "'Open Sans', Arial, Helvetica, sans-serif";
            }
        }

        return () => {
            unMountMicroAppCallback?.();
            unMountSystemThemeCallback?.();
        };
    });
};

export default useAppInitialization;
